import { Link } from 'react-router-dom'
import { ShieldCheck } from 'lucide-react'
import { useBranding } from '@/lib/branding'
import { cn } from '@/lib/cn'

interface OnboardingHeaderProps {
  className?: string
}

/**
 * Public-facing portal header. Uses branding from /api/v1/branding.
 * Primary color accent is driven by the --brand-primary CSS variable
 * set by BrandingProvider — we don't read primary_color directly.
 */
export function OnboardingHeader({ className }: OnboardingHeaderProps) {
  const { display_name, logo_url, support_url } = useBranding()

  return (
    <header
      className={cn(
        'sticky top-0 z-20 flex items-center justify-between gap-4 border-b border-border bg-bg/95 px-6 py-3 backdrop-blur-sm',
        className,
      )}
    >
      {/* Logo + name */}
      <div className="flex min-w-0 items-center gap-3">
        <div
          aria-hidden
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[color:var(--brand-primary,hsl(var(--primary)))]/10 text-[color:var(--brand-primary,hsl(var(--primary)))]"
        >
          {logo_url ? (
            <img
              src={logo_url}
              alt=""
              className="h-6 w-6 rounded-full object-contain"
              onError={(e) => {
                e.currentTarget.style.display = 'none'
              }}
            />
          ) : (
            <ShieldCheck size={16} aria-hidden />
          )}
        </div>
        <div className="min-w-0">
          <span className="block truncate text-sm font-semibold text-fg">
            {display_name}
          </span>
          <span className="block text-xs text-muted-fg">Developer Portal</span>
        </div>
      </div>

      {/* Right-side actions */}
      <div className="flex shrink-0 items-center gap-3">
        {support_url ? (
          <a
            href={support_url}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden text-xs text-muted-fg hover:text-fg hover:underline sm:block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:rounded"
          >
            Support
          </a>
        ) : null}
        <Link
          to="/login"
          className={cn(
            'inline-flex h-8 items-center rounded border border-border bg-transparent px-3 text-xs font-medium text-fg transition-colors',
            'hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1',
          )}
        >
          Operator Login
        </Link>
      </div>
    </header>
  )
}
